import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import VehicleModelOverview from '../../../../../types/retailer/VehicleModelOverview';
import VehicleModel from '../../../../../types/vehicle-model/VehicleModel';
import { RetailerService } from '../../../services/retailer.service';
import { VehicleModelSelectionCacheService } from '../../../services/vehicle-model-selection-cache.service';
import { RetailerTopBarComponent } from '../retailer-top-bar/retailer-top-bar.component';

@Component({
    selector: 'app-select-vehicle-model',
    imports: [
        RetailerTopBarComponent,
        NgClass,
        ReactiveFormsModule,
        FormsModule,
        Tooltip
    ],
    templateUrl: './select-vehicle-model.component.html',
    styleUrl: './select-vehicle-model.component.scss'
})
export class SelectVehicleModelComponent implements OnInit {
    models: string[] = [
        'All Vehicles',
        'Saloons',
        'SUVs & Offroaders',
        'Estates',
        'Hatchbacks',
        'Coupes',
        'Cabriolets',
        'MPVs'
    ];

    public vehicle_models: VehicleModel[] = [];
    public availableVehicleModels: VehicleModel[] = [];
    public availableBodyTypeCarClassModelSeriesCombinations: string[] = [];
    public selectedModel: string = 'All Vehicles';
    public search: string = '';
    public turningLightsOff: boolean = false;

    private searchSubject = new Subject<string>();

    constructor(
        private retailerService: RetailerService,
        private router: Router,
        private messageService: MessageService,
        private cacheService: VehicleModelSelectionCacheService
    ) {
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.loadVehicleModels();
        });
    }

    /**
     * Initializes the component.
     */
    ngOnInit(): void {
        this.search = this.cacheService.getSearch();
        this.selectedModel = this.cacheService.getSelectedModel();
        this.searchSubject.next(this.search);
    }

    /**
     * Handles the search input change.
     */
    public onSearchInputChange(): void {
        this.cacheService.setSearch(this.search);
        this.searchSubject.next(this.search);
    }

    /**
     * Handles the model selection.
     *
     * @param model The selected model.
     */
    public selectModel(model: string): void {
        this.selectedModel = model;
        this.cacheService.setSelectedModel(model);
        this.loadVehicleModels();
    }

    /**
     * Loads the vehicle models based on the search and selected model.
     */
    public loadVehicleModels(): void {
        this.retailerService.getVehicleModels(this.search, this.selectedModel)
            .then((vehicleModelOverview: VehicleModelOverview) => {
                this.vehicle_models = vehicleModelOverview.vehicleModels;
                this.availableVehicleModels = vehicleModelOverview.availableVehicleModels;
                this.availableBodyTypeCarClassModelSeriesCombinations = this.availableVehicleModels.map(
                    (vehicleModel: VehicleModel) => vehicleModel.bodyType?.name + '-' + vehicleModel.carClass?.name +
                        '-' + vehicleModel.modelSeries
                );

                // Sort vehicle_models so that available ones come first
                this.vehicle_models.sort((a, b) => {
                    const aAvailable = this.isAvailable(a) ? 0 : 1;
                    const bAvailable = this.isAvailable(b) ? 0 : 1;
                    return aAvailable - bAvailable;
                });
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000
                });
            });
    }

    /**
     * Checks if a vehicle model is available.
     *
     * @param vehicleModel The vehicle model to check.
     * @returns True if the vehicle model is available, false otherwise.
     */
    public isAvailable(vehicleModel: VehicleModel): boolean {
        return this.availableBodyTypeCarClassModelSeriesCombinations.includes(
            vehicleModel.bodyType?.name + '-' + vehicleModel.carClass?.name + '-' + vehicleModel.modelSeries
        );
    }

    /**
     * Navigates to the select details page with the selected vehicle model.
     *
     * @param vehicleModel The selected vehicle model.
     */
    public async selectVehicleModel(vehicleModel: VehicleModel): Promise<void> {
        await this.router.navigate([
            '/retailer/select-details/',
            vehicleModel.id
        ]);
    }

    /**
     * Turns off the lights in the studio.
     */
    public lightsOff() {
        if (this.turningLightsOff) {
            return;
        }
        this.turningLightsOff = true;
        this.retailerService.turnLightsOff().then(
            () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Lights turned off successfully.',
                    life: 3000
                });
            }
        ).catch((error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000
                });
            }
        ).finally(() => {
            this.turningLightsOff = false;
        });
    }

    /**
     * Handles the leave usecase action.
     * Clears the cache and navigates to the vehicle models page.
     */
    public onLeaveUsecase() {
        this.retailerService.leaveUsecase().then().catch();
        this.clearCache();
    }

    /**
     * Handles the event when the user changes the studio.
     * Clears the cache and reloads the vehicle models.
     */
    public onNewStudio() {
        this.loadVehicleModels();
    }

    /**
     * Clears the cache by removing the search and selected model from local storage.
     * Resets the search and selected model to their default values.
     */
    private clearCache(): void {
        this.cacheService.clear();
        this.search = '';
        this.selectedModel = 'All Vehicles';
        this.loadVehicleModels();
    }

}
